import { type ReactNode, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../../../shared/i18n/index.js';

// ── Shared presentational kit for the M05 Master Data (MDM) screens ─────────
// Additive helper used by the net-new MDM master/governance screens so they
// stay consistent, mobile-friendly (wrapping flex rows, touch-sized controls)
// and DRY. Purely presentational — no data fetching, no business logic.
// Existing lookup/taxation/holiday screens are untouched (Rule 1).
//
// The values below are the Company GST Configuration design. They are inline
// styles, which beat any stylesheet, so tokens/screen-theme.css cannot reach
// these screens — the look has to be carried here instead.

const ink = 'var(--color-text-primary)';
const muted = 'var(--color-text-muted)';
const border = 'var(--color-border-default)';
const accent = 'var(--color-brand-primary)';

export const s = {
  input: {
    padding: '7px 9px',
    borderRadius: 4,
    border: '1px solid var(--color-border-default)',
    background: 'var(--color-bg-surface)',
    color: ink,
    font: 'inherit',
    fontSize: 14,
    minHeight: 38,
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box' as const,
  } satisfies CSSProperties,
  label: { fontSize: 13, fontWeight: 600, color: muted, marginBottom: 6 } satisfies CSSProperties,
  primaryBtn: {
    padding: '7px 16px',
    borderRadius: 4,
    border: `1px solid ${accent}`,
    background: accent,
    color: 'var(--color-text-inverse)',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: 38,
  } satisfies CSSProperties,
  ghostBtn: {
    padding: '7px 12px',
    borderRadius: 4,
    border: '1px solid var(--color-border-default)',
    background: 'var(--color-bg-surface)',
    color: ink,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: 34,
  } satisfies CSSProperties,
  dangerBtn: {
    padding: '7px 12px',
    borderRadius: 4,
    border: '1px solid var(--color-danger)',
    background: 'var(--color-bg-surface)',
    color: 'var(--color-danger)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: 34,
  } satisfies CSSProperties,
  card: {
    border: `1px solid ${border}`,
    borderRadius: 4,
    padding: 18,
    marginBottom: 16,
    background: 'var(--color-bg-muted)',
  } satisfies CSSProperties,
  table: { width: '100%', border: `1px solid ${border}`, borderCollapse: 'collapse' as const, fontSize: 13 } satisfies CSSProperties,
  th: {
    padding: '10px 12px',
    textAlign: 'left' as const,
    background: 'var(--color-bg-muted)',
    color: 'var(--color-text-secondary)',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    borderBottom: `1px solid ${border}`,
  } satisfies CSSProperties,
  td: { padding: '10px 12px', borderBottom: `1px solid ${border}`, verticalAlign: 'top' as const } satisfies CSSProperties,
  muted,
};

/** Page shell: sets the unique <main aria-label> + heading + optional back link. */
export function MdmShell(props: {
  readonly label: string;
  readonly title: string;
  readonly description?: string;
  readonly backTo?: string;
  readonly backLabel?: string;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <main aria-label={props.label} style={{ maxWidth: 1440, color: ink, fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif' }}>
      <div style={{
        display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap',
        borderBottom: `1px solid ${border}`, paddingBottom: 18, marginBottom: 18,
      }}>
        {props.backTo && (
          <Link to={props.backTo} data-testid="mdm-back">← {props.backLabel ?? t('mdm.kit.masterData')}</Link>
        )}
        <h1 style={{ fontFamily: '"IBM Plex Serif", Georgia, serif', fontSize: 28, fontWeight: 600, margin: 0 }}>{props.title}</h1>
        <div style={{ marginLeft: 'auto' }}>{props.actions}</div>
      </div>
      {props.description && (
        <p style={{ color: muted, marginBottom: 16, fontSize: 13 }}>{props.description}</p>
      )}
      {props.children}
    </main>
  );
}

/** Labelled form field wrapper. */
export function Field(props: {
  readonly id: string;
  readonly label: string;
  readonly children: ReactNode;
  readonly hint?: string;
}): ReactNode {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160, flex: '1 1 180px' }}>
      <label htmlFor={props.id} style={s.label}>{props.label}</label>
      {props.children}
      {props.hint && <span style={{ fontSize: 11, color: muted, marginTop: 2 }}>{props.hint}</span>}
    </div>
  );
}

/** Coloured status pill. */
export function StatusBadge({ status }: { readonly status: string }): ReactNode {
  const map: Record<string, { bg: string; fg: string }> = {
    ACTIVE: { bg: 'var(--color-success-bg)', fg: 'var(--color-success)' },
    INACTIVE: { bg: 'var(--color-bg-muted)', fg: 'var(--color-text-secondary)' },
    ARCHIVED: { bg: 'var(--color-bg-muted)', fg: 'var(--color-text-secondary)' },
    PENDING_APPROVAL: { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning)' },
    REJECTED: { bg: 'var(--color-danger-bg)', fg: 'var(--color-danger)' },
    APPROVED: { bg: 'var(--color-success-bg)', fg: 'var(--color-success)' },
    COMPLETED: { bg: 'var(--color-success-bg)', fg: 'var(--color-success)' },
    FAILED: { bg: 'var(--color-danger-bg)', fg: 'var(--color-danger)' },
    VALIDATION_FAILED: { bg: 'var(--color-danger-bg)', fg: 'var(--color-danger)' },
    ROLLED_BACK: { bg: 'var(--color-bg-muted)', fg: 'var(--color-text-secondary)' },
    UPLOADED: { bg: 'var(--color-info-bg)', fg: 'var(--color-text-link)' },
    VALIDATED: { bg: 'var(--color-info-bg)', fg: 'var(--color-text-link)' },
    IMPORTING: { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning)' },
    VALIDATING: { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning)' },
  };
  const c = map[status] ?? { bg: 'var(--color-bg-muted)', fg: 'var(--color-text-secondary)' };
  return (
    <span style={{ background: c.bg, color: c.fg, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
      {status}
    </span>
  );
}

/** Inline error alert. */
export function ErrorAlert({ message }: { readonly message: string | null }): ReactNode {
  if (!message) return null;
  return <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{message}</p>;
}
